import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { allItems, formatViews } from '@/hooks/useContent';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allItems
      .filter((item) => item.type !== 'special')
      .filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.tags.some((tag) => tag.toLowerCase().includes(q)),
      )
      .slice(0, 6);
  }, [query]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-20 md:pt-28" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-secondary-950/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background-50 rounded-lg border border-background-200 overflow-hidden animate-fade-up">
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 md:px-5 py-3.5 border-b border-background-200">
          <i className="ri-search-line text-lg text-primary-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            type="text"
            name="q"
            placeholder="搜索资讯、视频、话题关键词…"
            className="flex-1 bg-transparent text-sm md:text-base text-foreground-950 placeholder:text-foreground-400 outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 cursor-pointer transition-colors"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </form>

        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim() && suggestions.length === 0 ? (
            <p className="px-5 py-6 text-sm text-foreground-400 text-center">没有找到相关内容，换个关键词试试</p>
          ) : null}

          {!query.trim() ? (
            <div className="px-5 py-5">
              <p className="text-xs text-foreground-400 mb-3">大家都在搜</p>
              <div className="flex flex-wrap gap-2">
                {['音乐节', '原创动画', '短剧', '现场演出', '治愈系'].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setQuery(tag)}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-700 hover:bg-accent-100 hover:text-accent-900 cursor-pointer transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onClose();
                navigate(`/detail/${item.id}`);
              }}
              className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-background-100 cursor-pointer transition-colors"
            >
              <span className="w-10 h-10 rounded-md overflow-hidden bg-background-200 shrink-0">
                <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-foreground-950 truncate">{item.title}</span>
                <span className="block text-xs text-foreground-400 mt-0.5">
                  {item.author} · {formatViews(item.views)} 热度
                </span>
              </span>
              <i className="ri-arrow-right-s-line text-foreground-300" />
            </button>
          ))}

          {query.trim() ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/search?q=${encodeURIComponent(query.trim())}`);
              }}
              className="w-full px-5 py-3.5 text-sm font-medium text-primary-600 hover:bg-primary-50 cursor-pointer transition-colors border-t border-background-200"
            >
              查看“{query.trim()}”的全部结果
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}